import { useMemo, useState, useEffect } from "react";
import { BookOpen, ExternalLink, ChevronDown, Volume2, Square } from "lucide-react";
import { speak, stopSpeaking, narratorAvailable } from "../lib/narrator";

const IDS = {
  section: "depth-cross-section",
  dot: "cross-dot",
};

// Palette matches the map's depth bins.
const DEPTH_BINS = [
  { max: 15, color: "hsl(14 66% 32%)", label: "0", tip: "he feels" },
  { max: 40, color: "hsl(22 60% 40%)", label: "15" },
  { max: 100, color: "hsl(30 28% 32%)", label: "40" },
  { max: 300, color: "hsl(210 14% 42%)", label: "100" },
  { max: 700, color: "hsl(218 22% 55%)", label: "300" },
];
const depthColor = (d) => {
  if (d == null) return "hsl(30 28% 32%)";
  for (const b of DEPTH_BINS) if (d <= b.max) return b.color;
  return DEPTH_BINS[DEPTH_BINS.length - 1].color;
};

// A piecewise depth-to-y mapping so shallow quakes get plenty of vertical
// room and deep ones still fit. Depths in km; y in the SVG viewBox.
const buildDepthScale = (surfaceY, floorY) => {
  const usable = floorY - surfaceY;
  // Fraction of vertical each band gets
  const bands = [
    { min: 0, max: 15, frac: 0.22 },
    { min: 15, max: 40, frac: 0.16 },
    { min: 40, max: 100, frac: 0.2 },
    { min: 100, max: 300, frac: 0.22 },
    { min: 300, max: 700, frac: 0.2 },
  ];
  let acc = surfaceY;
  const spans = bands.map((b) => {
    const y0 = acc;
    const y1 = acc + usable * b.frac;
    acc = y1;
    return { ...b, y0, y1 };
  });
  const scale = (d) => {
    if (d == null || d < 0) return surfaceY;
    if (d >= 700) return floorY;
    for (const s of spans) {
      if (d <= s.max) {
        const t = (d - s.min) / (s.max - s.min);
        return s.y0 + t * (s.y1 - s.y0);
      }
    }
    return floorY;
  };
  return { scale, spans };
};

const magR = (m) => {
  if (m == null) return 2;
  return Math.max(1.8, Math.min(9, Math.pow(m, 1.3) * 0.7));
};

const LNG_MIN = 165.5;
const LNG_MAX = 179.5;

// Wobbly ground line built from a seeded sine so it looks hand-drawn.
const groundPath = (width, y0) => {
  const pts = [];
  const N = 60;
  for (let i = 0; i <= N; i++) {
    const x = (i / N) * width;
    const wobble = Math.sin(i * 0.9) * 1.3 + Math.sin(i * 1.7 + 3) * 0.9;
    pts.push([x, y0 + wobble]);
  }
  return `M ${pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" L ")}`;
};

export default function DepthCrossSection({ quakes = [], slice }) {
  const VIEW_W = 1100;
  const VIEW_H = 280;
  const PAD_LEFT = 66;
  const PAD_RIGHT = 24;
  const SURFACE_Y = 42;
  const FLOOR_Y = VIEW_H - 24;
  const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT;

  const [storyOpen, setStoryOpen] = useState(false);
  const [narrating, setNarrating] = useState(false);
  const canNarrate = narratorAvailable();

  // Stop narration if the visitor changes preset or closes the slice
  useEffect(() => {
    stopSpeaking();
    setNarrating(false);
  }, [slice?.presetId, slice?.active]);

  // Also stop on unmount
  useEffect(() => () => stopSpeaking(), []);

  const startNarration = () => {
    if (!slice?.presetName) return;
    const parts = [
      `${slice.presetName}.`,
      slice.presetNarration || "",
      ...(slice.presetStory || []),
    ]
      .filter(Boolean)
      .join(" ");
    const ok = speak(parts, {
      onStart: () => setNarrating(true),
      onEnd: () => setNarrating(false),
    });
    if (!ok) setNarrating(false);
  };

  const stopNarration = () => {
    stopSpeaking();
    setNarrating(false);
  };

  const sliceActive = !!slice?.active;
  const theta = slice?.theta ?? 0;
  const sliceLng = slice?.lng ?? 174.5;
  const sliceLat = slice?.lat ?? -41;
  const halfWidth = slice?.halfWidth ?? 0.6;
  const sinT = Math.sin(theta);
  const cosT = Math.cos(theta);

  const { scale, spans } = useMemo(
    () => buildDepthScale(SURFACE_Y, FLOOR_Y),
    []
  );

  // Along/perp coordinates of a quake relative to the slice centre.
  // In lng/lat space with theta measured clockwise from north:
  //   along = dLng * sin(theta) + dLat * cos(theta)  (positive toward the
  //           handle-end / "top" of the transect)
  //   perp  = dLng * cos(theta) - dLat * sin(theta)  (signed side)
  const sliceCoords = (q) => {
    const dLng = q.longitude - sliceLng;
    const dLat = q.latitude - sliceLat;
    return {
      along: dLng * sinT + dLat * cosT,
      perp: dLng * cosT - dLat * sinT,
    };
  };

  // When the slice is inactive: full W-E view. When active: filter to
  // quakes whose perpendicular distance from the transect is ≤ halfWidth
  // (in degrees) and project them onto the along-transect axis.
  const HALF_LEN_DEG = 7;
  const visible = quakes.filter((q) => {
    if (typeof q.longitude !== "number" || typeof q.depth !== "number") return false;
    if (sliceActive) {
      if (typeof q.latitude !== "number") return false;
      const { along, perp } = sliceCoords(q);
      return Math.abs(perp) <= halfWidth && Math.abs(along) <= HALF_LEN_DEG;
    }
    return q.longitude >= LNG_MIN - 2 && q.longitude <= LNG_MAX + 2;
  });

  const LAT_MIN = -47.5;
  const LAT_MAX = -33.5;

  const xToPlot = (q) => {
    if (sliceActive) {
      const { along } = sliceCoords(q);
      const clamped = Math.max(-HALF_LEN_DEG, Math.min(HALF_LEN_DEG, along));
      // "Top" end of handle sits at along = +HALF_LEN_DEG -> left of plot
      return PAD_LEFT + ((HALF_LEN_DEG - clamped) / (HALF_LEN_DEG * 2)) * PLOT_W;
    }
    const clamped = Math.max(LNG_MIN, Math.min(LNG_MAX, q.longitude));
    return PAD_LEFT + ((clamped - LNG_MIN) / (LNG_MAX - LNG_MIN)) * PLOT_W;
  };

  const lngToX = (lng) => {
    const clamped = Math.max(LNG_MIN, Math.min(LNG_MAX, lng));
    return PAD_LEFT + ((clamped - LNG_MIN) / (LNG_MAX - LNG_MIN)) * PLOT_W;
  };
  // A short compass label from an azimuth degree (0=N, clockwise).
  const compassLabel = (deg) => {
    const d = ((deg % 360) + 360) % 360;
    const points = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
    return points[Math.round(d / 22.5) % 16];
  };
  const bearingDeg = (theta * 180) / Math.PI;
  const topLabel = compassLabel(bearingDeg);
  const bottomLabel = compassLabel(bearingDeg + 180);

  // Sort so deepest (cool) drawn first, shallow (warm) on top
  const ordered = [...visible].sort((a, b) => (b.depth ?? 0) - (a.depth ?? 0));

  const shallowCount = visible.filter((q) => (q.depth ?? Infinity) <= 15).length;
  const deepestQuake = visible.reduce(
    (m, q) => (q.depth != null && q.depth > (m?.depth ?? -1) ? q : m),
    null
  );

  return (
    <section
      data-testid={IDS.section}
      className="relative border border-[hsl(var(--ink))]/40 bg-[hsl(var(--paper-2))]/50 overflow-hidden"
    >
      <header className="flex items-baseline justify-between px-5 py-3 border-b border-[hsl(var(--ink))]/25 gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--ink-2))]">
            {sliceActive
              ? slice?.presetName
                ? `Preset · ${slice.presetName}`
                : `Transect · ${topLabel}–${bottomLabel}`
              : "Cross-section"}
          </div>
          <div className="font-display italic text-lg mt-0.5">
            {sliceActive
              ? `Along ${bearingDeg.toFixed(0)}° through ${sliceLng.toFixed(2)}°E, ${Math.abs(sliceLat).toFixed(2)}°S`
              : "Beneath his belly"}
          </div>
          {slice?.presetNarration && (
            <p
              data-testid="preset-narration"
              className="font-display italic text-[14px] leading-snug text-[hsl(var(--ink))]/85 mt-2 max-w-2xl border-l-2 border-[hsl(var(--seismic))] pl-3"
            >
              &ldquo;{slice.presetNarration}&rdquo;
            </p>
          )}
          {slice?.presetStory && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {canNarrate && (
                <button
                  data-testid="listen-btn"
                  onClick={narrating ? stopNarration : startNarration}
                  aria-pressed={narrating}
                  className={`mono text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 border transition-colors flex items-center gap-1.5 ${
                    narrating
                      ? "bg-[hsl(var(--seismic))] text-[hsl(var(--paper))] border-[hsl(var(--seismic))]"
                      : "border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5 hover:border-[hsl(var(--seismic))]/60"
                  }`}
                >
                  {narrating ? (
                    <>
                      <Square size={10} fill="currentColor" />
                      Stop
                      <span className="listen-wave" aria-hidden>
                        <i /><i /><i /><i />
                      </span>
                    </>
                  ) : (
                    <>
                      <Volume2 size={11} />
                      Listen
                    </>
                  )}
                </button>
              )}
              <button
                data-testid="read-more-toggle"
                onClick={() => setStoryOpen((v) => !v)}
                className="mono text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 border border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5 hover:border-[hsl(var(--seismic))]/60 transition-colors flex items-center gap-1.5"
                aria-expanded={storyOpen}
              >
                <BookOpen size={11} />
                {storyOpen ? "Close story" : "Read more"}
                <ChevronDown
                  size={11}
                  style={{
                    transform: storyOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 200ms ease",
                  }}
                />
              </button>
              {slice.presetLearnMoreUrl && (
                <a
                  data-testid="learn-more-link"
                  href={slice.presetLearnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mono text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 border border-[hsl(var(--ink))]/40 hover:bg-[hsl(var(--ink))]/5 hover:border-[hsl(var(--seismic))]/60 transition-colors flex items-center gap-1.5"
                >
                  <ExternalLink size={11} />
                  {slice.presetLearnMoreLabel || "External link"}
                </a>
              )}
            </div>
          )}
        </div>
        <div className="hidden sm:flex items-baseline gap-5 mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-mid))]">
          <span>
            Shallow <span className="text-[hsl(var(--seismic))]">{shallowCount}</span>
          </span>
          {deepestQuake && (
            <span>
              Deepest{" "}
              <span className="text-[hsl(var(--ink))]">
                {Math.round(deepestQuake.depth)} km
              </span>
            </span>
          )}
          <span>{visible.length} plotted</span>
        </div>
      </header>

      <div className="p-3 sm:p-5">
        {storyOpen && slice?.presetStory && (
          <div
            data-testid="preset-story"
            className="mb-4 border border-[hsl(var(--ink))]/25 bg-[hsl(var(--paper))]/60 p-5 space-y-3 max-w-4xl fade-in-up"
          >
            <div className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--seismic))]">
              About the {slice.presetName}
            </div>
            {slice.presetStory.map((para, i) => (
              <p key={`${slice.presetId}-p${i}`} className="text-[15px] leading-relaxed text-[hsl(var(--ink))]/90">
                {para}
              </p>
            ))}
            {slice.presetLearnMoreUrl && (
              <a
                href={slice.presetLearnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--seismic))] hover:underline"
              >
                <ExternalLink size={11} /> {slice.presetLearnMoreLabel}
              </a>
            )}
          </div>
        )}
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          className="w-full h-auto"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="soilHatch"
              width="7"
              height="7"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(35)"
            >
              <line x1="0" y1="0" x2="0" y2="7" stroke="hsl(30 20% 40%)" strokeWidth="0.35" opacity="0.35" />
            </pattern>
          </defs>

          {/* Stratum bands with subtle depth-tinted fills */}
          {spans.map((s) => (
            <g key={s.min}>
              <rect
                x={PAD_LEFT}
                y={s.y0}
                width={PLOT_W}
                height={s.y1 - s.y0}
                fill={depthColor(s.min + 0.001)}
                opacity="0.06"
              />
              <line
                x1={PAD_LEFT}
                y1={s.y1}
                x2={VIEW_W - PAD_RIGHT}
                y2={s.y1}
                stroke="hsl(24 22% 10%)"
                strokeWidth="0.4"
                opacity="0.35"
                strokeDasharray="2 4"
              />
              {/* Depth label on left */}
              <text
                x={PAD_LEFT - 8}
                y={s.y0 + 4}
                textAnchor="end"
                className="mono"
                fontSize="9"
                fill="hsl(24 22% 30%)"
              >
                {s.min === 0 ? "0" : `${s.min}`}
              </text>
              {s.min === 0 && (
                <text
                  x={PAD_LEFT - 8}
                  y={s.y0 - 6}
                  textAnchor="end"
                  className="mono"
                  fontSize="8"
                  fill="hsl(24 22% 45%)"
                  letterSpacing="1"
                >
                  km
                </text>
              )}
            </g>
          ))}
          {/* Bottom depth label */}
          <text
            x={PAD_LEFT - 8}
            y={FLOOR_Y + 4}
            textAnchor="end"
            className="mono"
            fontSize="9"
            fill="hsl(24 22% 30%)"
          >
            700
          </text>

          {/* Soil hatching in the underground area */}
          <rect
            x={PAD_LEFT}
            y={SURFACE_Y}
            width={PLOT_W}
            height={FLOOR_Y - SURFACE_Y}
            fill="url(#soilHatch)"
            opacity="0.6"
          />

          {/* Ground line -- wavy, hand-drawn */}
          <path
            d={groundPath(VIEW_W, SURFACE_Y)}
            fill="none"
            stroke="hsl(24 22% 10%)"
            strokeWidth="1.4"
          />
          {/* Ground shadow underline (double-ink) */}
          <path
            d={groundPath(VIEW_W, SURFACE_Y + 2.5)}
            fill="none"
            stroke="hsl(24 22% 10%)"
            strokeWidth="0.5"
            opacity="0.5"
          />

          {/* Tiny sleeping dog silhouette resting on the surface, centre-ish */}
          <g transform={`translate(${PAD_LEFT + PLOT_W * 0.5 - 22} ${SURFACE_Y - 22})`} opacity="0.85">
            {/* Body */}
            <ellipse cx="22" cy="18" rx="20" ry="9" fill="hsl(41 44% 88%)" stroke="hsl(24 22% 10%)" strokeWidth="1" />
            {/* Head */}
            <ellipse cx="6" cy="17" rx="7" ry="6" fill="hsl(41 44% 88%)" stroke="hsl(24 22% 10%)" strokeWidth="1" />
            {/* Ear */}
            <ellipse cx="7" cy="12" rx="4" ry="2" transform="rotate(-25 7 12)" fill="hsl(38 32% 78%)" stroke="hsl(24 22% 10%)" strokeWidth="0.8" />
            {/* Snout */}
            <ellipse cx="1" cy="19" rx="2.4" ry="1.6" fill="hsl(41 44% 88%)" stroke="hsl(24 22% 10%)" strokeWidth="0.8" />
            {/* Closed eye */}
            <path d="M 6 15 q 2 -1.2 4 0" fill="none" stroke="hsl(24 22% 10%)" strokeWidth="0.7" />
            {/* Fur strokes on top of body */}
            <g stroke="hsl(24 22% 10%)" strokeWidth="0.4" opacity="0.6">
              {[6, 10, 14, 18, 22, 26, 30, 34, 38].map((x) => (
                <line key={x} x1={x} y1="12" x2={x + 0.4} y2="8" />
              ))}
            </g>
            <text x="0" y="34" fontSize="7" fill="hsl(24 22% 25%)" className="mono" letterSpacing="1">
              him
            </text>
          </g>

          {/* Axis ticks: longitude when unsliced, along-transect degrees when sliced */}
          {(sliceActive ? [5, 2.5, 0, -2.5, -5] : [168, 172, 176]).map((v) => {
            const xPos = sliceActive
              ? PAD_LEFT + ((HALF_LEN_DEG - v) / (HALF_LEN_DEG * 2)) * PLOT_W
              : lngToX(v);
            const label = sliceActive
              ? v === 0
                ? "•"
                : `${v > 0 ? "+" : ""}${v}°`
              : `${v}°E`;
            return (
              <g key={v}>
                <line
                  x1={xPos}
                  y1={SURFACE_Y}
                  x2={xPos}
                  y2={FLOOR_Y}
                  stroke="hsl(24 22% 10%)"
                  strokeWidth="0.3"
                  opacity={sliceActive && v === 0 ? 0.45 : 0.2}
                  strokeDasharray="1 4"
                />
                <text
                  x={xPos}
                  y={SURFACE_Y - 6}
                  textAnchor="middle"
                  fontSize="8"
                  className="mono"
                  fill="hsl(24 22% 40%)"
                >
                  {label}
                </text>
              </g>
            );
          })}

          {/* Direction labels at the ends */}
          <text
            x={PAD_LEFT}
            y={FLOOR_Y + 14}
            fontSize="8"
            className="mono"
            fill="hsl(24 22% 35%)"
            letterSpacing="2"
          >
            {sliceActive ? topLabel : "WEST"}
          </text>
          <text
            x={VIEW_W - PAD_RIGHT}
            y={FLOOR_Y + 14}
            textAnchor="end"
            fontSize="8"
            className="mono"
            fill="hsl(24 22% 35%)"
            letterSpacing="2"
          >
            {sliceActive ? bottomLabel : "EAST"}
          </text>

          {/* Tremor dots -- vertical drop line + circle */}
          {ordered.map((q, i) => {
            const x = xToPlot(q);
            const y = scale(q.depth);
            const r = magR(q.magnitude);
            const c = depthColor(q.depth);
            const isShallow = q.depth <= 15;
            return (
              <g key={q.publicID || i} data-testid={IDS.dot}>
                {/* Drop line from surface to dot for shallow quakes only
                   (gives visceral sense they're right beneath the ground) */}
                {isShallow && (
                  <line
                    x1={x}
                    y1={SURFACE_Y + 1}
                    x2={x}
                    y2={y}
                    stroke={c}
                    strokeWidth="0.5"
                    opacity="0.5"
                    strokeDasharray="1 2"
                  />
                )}
                <circle
                  cx={x}
                  cy={y}
                  r={r}
                  fill={c}
                  stroke="hsl(24 22% 6%)"
                  strokeWidth="0.4"
                  opacity="0.92"
                />
              </g>
            );
          })}
        </svg>
      </div>

      <footer className="px-5 py-3 border-t border-[hsl(var(--ink))]/25 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1">
        <div className="mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--ink-2))]">
          {sliceActive
            ? `Transect · ${bearingDeg.toFixed(0)}° · ±${halfWidth.toFixed(2)}° corridor · depth compressed`
            : "West-East slice · depth compressed"} &middot; MMI ≥ 3
        </div>
        <div className="font-display italic text-sm text-[hsl(var(--ink))]/85">
          The shallow ones knock on his ribs. The deep ones only whisper.
        </div>
      </footer>
    </section>
  );
}
